/**
 * Turns a /api/spiral/scan response into chart overlays.
 *
 * Pure functions, no React and no chart instance, for the same reason elliottOverlay.js is: the
 * mapping from a scan result to what gets drawn is the part most likely to be wrong, and it is only
 * testable if it does not need a rendered chart to run.
 *
 * Three overlays make up the visual, each replaceable on its own:
 *
 *   spiral-band     the gold zone across the convergence window
 *   spiral-pivots   a marker on each contributing pivot
 *   spiral-arcs     a curve from each pivot to the zone
 *
 * All three carry fixed ids so a re-run REPLACES rather than stacks. A second scan leaving the
 * first one's band behind would show two zones and no way to tell which belonged to the current
 * result.
 */

export const SPIRAL_BAND_ID = 'spiral-band';
export const SPIRAL_PIVOTS_ID = 'spiral-pivots';
export const SPIRAL_ARCS_ID = 'spiral-arcs';

/**
 * How many zones can be drawn at once.
 *
 * Bounded by the palette, not by the data: a seventh colour would have to repeat one already in
 * use, and two zones sharing a colour is worse than one zone missing — the whole point of colouring
 * them is to tell which pivots agree with which.
 */
export const SPIRAL_MAX_ZONES = 6;

/**
 * One colour per set of agreeing pivots, so a chart carrying several convergences stays readable.
 *
 * Distinct hues rather than shades of one, because the zones are not ordered on a scale — they are
 * separate claims that happen to be near each other in time, and a gradient would imply a ranking
 * the colours do not carry. Amber leads because it is the colour the single-zone version used.
 */
export const SPIRAL_ZONE_COLORS = [
  { band: 'rgba(255, 193, 7, 0.28)',  border: 'rgba(255, 152, 0, 0.95)', arc: 'rgba(255, 152, 0, 0.85)' },
  { band: 'rgba(0, 172, 193, 0.24)',  border: 'rgba(0, 131, 143, 0.95)', arc: 'rgba(0, 131, 143, 0.85)' },
  { band: 'rgba(156, 39, 176, 0.22)', border: 'rgba(123, 31, 162, 0.95)', arc: 'rgba(123, 31, 162, 0.85)' },
  { band: 'rgba(233, 30, 99, 0.22)',  border: 'rgba(194, 24, 91, 0.95)', arc: 'rgba(194, 24, 91, 0.85)' },
  { band: 'rgba(124, 179, 66, 0.24)', border: 'rgba(85, 139, 47, 0.95)', arc: 'rgba(85, 139, 47, 0.85)' },
  { band: 'rgba(63, 81, 181, 0.22)',  border: 'rgba(48, 63, 159, 0.95)', arc: 'rgba(48, 63, 159, 0.85)' },
];

export function spiralZoneColor(index) {
  return SPIRAL_ZONE_COLORS[index % SPIRAL_ZONE_COLORS.length];
}

/** Per-zone overlay ids. Zone 0 keeps the original ids so a single-zone draw is unchanged. */
export function spiralOverlayIds(index) {
  const suffix = index === 0 ? '' : `-${index}`;
  return {
    band: `${SPIRAL_BAND_ID}${suffix}`,
    pivots: `${SPIRAL_PIVOTS_ID}${suffix}`,
    arcs: `${SPIRAL_ARCS_ID}${suffix}`,
  };
}

/**
 * Every id a multi-zone draw can produce.
 *
 * A fixed list rather than one derived from the current result, for the same reason the inspection
 * lines use one: clearing has to remove the PREVIOUS draw, which the caller no longer holds. Four
 * zones followed by two would otherwise leave two orphaned bands on the chart.
 */
export const SPIRAL_OVERLAY_IDS = Array.from({ length: SPIRAL_MAX_ZONES }, (_, i) => spiralOverlayIds(i))
  .flatMap(ids => [ids.band, ids.pivots, ids.arcs]);

// The band, border and arc colours now come from SPIRAL_ZONE_COLORS above, one entry per zone.
// The single amber pair that used to live here is entry 0, so a one-zone draw is unchanged.
const HIGH_COLOR = '#c62828';
const LOW_COLOR = '#2e7d32';

/**
 * The chart's date for a bar index, or null when the index lies past the last bar.
 *
 * <p>The scan deliberately projects PAST the end of the series - an upcoming convergence is the
 * point of it - so a target bar routinely has no date on the chart. The caller decides what to do
 * about that; this returns null rather than inventing one, because a fabricated future date would
 * be drawn as though it were a real bar.
 */
export function dateForBarIndex(dates, barIndex) {
  if (!Array.isArray(dates) || dates.length === 0) return null;
  if (barIndex < 0 || barIndex >= dates.length) return null;
  return dates[barIndex];
}

/**
 * Clamps a zone to the dates the chart can actually draw.
 *
 * <p>A forecast zone may sit entirely beyond the last bar, in which case there is nothing to draw
 * and this returns null. When it straddles the edge, the visible part is drawn and
 * {@code truncated} says so, so the caller can tell the user the zone extends past the chart
 * rather than silently showing a narrower band than the scan found.
 */
export function resolveZoneDates(dates, zone) {
  if (!zone || !Array.isArray(dates) || dates.length === 0) return null;
  const lastIndex = dates.length - 1;
  const from = Math.max(0, zone.fromBarIndex);
  const to = zone.toBarIndex;
  if (from > lastIndex) {
    // Entirely in the future. Nothing on the chart corresponds to it.
    return { date1: null, date2: null, truncated: true, entirelyBeyond: true };
  }
  return {
    date1: dates[from],
    date2: dates[Math.min(to, lastIndex)],
    truncated: to > lastIndex,
    entirelyBeyond: false,
  };
}

/**
 * The gold band across the convergence window.
 *
 * <p>Returns null when the zone cannot be placed on the chart, so a caller adding the result
 * unconditionally draws nothing rather than a band at a default position.
 */
export function buildSpiralBandOverlay(zone, dates, index = 0) {
  const span = resolveZoneDates(dates, zone);
  if (!span || span.entirelyBeyond) return null;

  const colors = spiralZoneColor(index);
  // The date is in the label because several bands can now be on screen at once, and "3 pivots"
  // alone does not say which band a reader is looking at.
  const when = zone.targetDate ? ` · ${zone.targetDate}` : '';
  return {
    type: 'areaHighlight',
    id: spiralOverlayIds(index).band,
    date1: span.date1,
    date2: span.date2,
    color: colors.band,
    borderColor: colors.border,
    label: `Φ Zone${when} · ${zone.convergenceCount} pivots`,
    source: 'spiralScan',
  };
}

/**
 * A marker on each pivot that contributed to the zone.
 *
 * <p>Highs are marked above the bar and lows below, in the colours the rest of the app uses for
 * them, so the direction of each contributing turn is readable without opening the tooltip. The
 * label is the Fibonacci step, which is what makes the marker mean something: "F(233)" says this
 * turn is reaching nineteen years forward.
 */
export function buildSpiralPivotOverlay(zone, dates, index = 0) {
  if (!zone || !Array.isArray(zone.hits) || zone.hits.length === 0) return null;

  const points = [];
  const seen = new Set();
  zone.hits.forEach((hit) => {
    const date = dateForBarIndex(dates, hit.pivotBarIndex);
    if (!date) return;
    // One marker per pivot bar. A bar that is both a HIGH and a LOW would otherwise get two
    // markers stacked on the same point, which reads as a rendering fault.
    if (seen.has(hit.pivotBarIndex)) return;
    seen.add(hit.pivotBarIndex);

    const isHigh = hit.pivotType === 'HIGH';
    points.push({
      date,
      price: hit.pivotPrice,
      position: isHigh ? 'above' : 'below',
      color: isHigh ? HIGH_COLOR : LOW_COLOR,
      label: `F(${hit.fibNumber})`,
    });
  });

  if (points.length === 0) return null;
  return {
    type: 'pivotMarkers',
    id: spiralOverlayIds(index).pivots,
    points,
    color: spiralZoneColor(index).band,
    label: 'Φ pivots',
    source: 'spiralScan',
  };
}

/**
 * A curve from each contributing pivot to the convergence zone.
 *
 * <p>Curved rather than straight because these vectors span years and would otherwise be a fan of
 * near-horizontal lines through the candles, indistinguishable from a trendline study. An arc
 * lifts them clear and makes the "reaching forward" relationship legible.
 *
 * <p>Each arc ends at the LAST DRAWABLE bar when the zone is in the future. Drawing to a bar that
 * does not exist would put the arc at an arbitrary place; ending at the chart edge, pointing at the
 * band, is the honest depiction of "this reaches past what you can see".
 */
export function buildSpiralArcOverlay(zone, dates, ohlc, index = 0) {
  if (!zone || !Array.isArray(zone.hits) || zone.hits.length === 0) return null;
  if (!Array.isArray(dates) || dates.length === 0) return null;

  const lastIndex = dates.length - 1;
  const targetIndex = Math.min(zone.targetBarIndex, lastIndex);
  const targetDate = dates[targetIndex];
  if (!targetDate) return null;

  // Anchor the arc's far end near the top of the visible range so the curves converge on the band
  // rather than terminating inside the candles.
  const targetPrice = highestOf(ohlc, Math.max(0, targetIndex - 5), targetIndex);

  const arcs = [];
  const seen = new Set();
  zone.hits.forEach((hit) => {
    if (seen.has(hit.pivotBarIndex)) return;
    seen.add(hit.pivotBarIndex);
    const fromDate = dateForBarIndex(dates, hit.pivotBarIndex);
    if (!fromDate || fromDate === targetDate) return;
    arcs.push({
      date1: fromDate,
      price1: hit.pivotPrice,
      date2: targetDate,
      price2: targetPrice,
      fibNumber: hit.fibNumber,
      // THE ORIGIN DATE, carried for the floater. `date1` already is it, but the renderer's tooltip
      // reads a named field rather than a coordinate: which end of a two-point line is the "origin"
      // is a fact about this study, and a renderer inferring it from coords[0] would be inferring
      // it for every `lines` series in the chart.
      originDate: fromDate,
      // The rung, so the floater can say WHY this pivot reaches this bar rather than only that it
      // does. It is the whole content of the study - a Fibonacci multiple of the lunar month.
      fibMultiple: hit.fibNumber,
    });
  });

  if (arcs.length === 0) return null;
  return {
    type: 'spiralArcs',
    id: spiralOverlayIds(index).arcs,
    arcs,
    // Arcs match their band, which is what makes "these pivots agree on THAT date" legible when
    // several convergences overlap. Without it the arcs are one tangle with no owner.
    color: spiralZoneColor(index).arc,
    label: 'Φ vectors',
    source: 'spiralScan',
  };
}

/** Highest high across a small window, used to lift the arc endpoints above the candles. */
function highestOf(ohlc, fromIndex, toIndex) {
  if (!Array.isArray(ohlc) || ohlc.length === 0) return 0;
  let best = 0;
  for (let i = Math.max(0, fromIndex); i <= Math.min(toIndex, ohlc.length - 1); i++) {
    const bar = ohlc[i];
    // ECharts candlestick order is [open, close, low, high].
    const high = Array.isArray(bar) ? bar[3] : undefined;
    if (typeof high === 'number' && high > best) best = high;
  }
  return best;
}

/**
 * Everything the chart should draw for one scan result, in draw order.
 *
 * <p>Returns an empty array when the result has no usable zone, so the caller can add the whole
 * list unconditionally and get nothing rather than a half-drawn study.
 */
export function buildSpiralOverlays(zone, barData, index = 0) {
  const dates = barData?.dates;
  const ohlc = barData?.ohlc;
  if (!zone || !Array.isArray(dates) || dates.length === 0) return [];

  return [
    buildSpiralArcOverlay(zone, dates, ohlc, index),   // under the band and markers
    buildSpiralBandOverlay(zone, dates, index),
    buildSpiralPivotOverlay(zone, dates, index),
  ].filter(Boolean);
}

/**
 * Everything to draw for SEVERAL zones at once, each in its own colour.
 *
 * <p>The near-term window routinely returns more than one convergence — around SPY's August 2026
 * top there were two within four days of each other — and drawing them all in the same amber makes
 * one indistinguishable tangle. The colour is what says "these pivots agree on THAT date", which is
 * the whole reading.
 *
 * <p>Capped at the palette size. Zones arrive strongest-first, so the cap drops the weakest, and
 * {@link #countUndrawnZones} lets the caller say how many rather than silently showing fewer.
 */
export function buildMultiSpiralOverlays(zones, barData) {
  if (!Array.isArray(zones) || zones.length === 0) return [];
  return zones
    .slice(0, SPIRAL_MAX_ZONES)
    .flatMap((zone, i) => buildSpiralOverlays(zone, barData, i));
}

/** How many zones the palette could not take. Reported in words rather than dropped in silence. */
export function countUndrawnZones(zones) {
  if (!Array.isArray(zones)) return 0;
  return Math.max(0, zones.length - SPIRAL_MAX_ZONES);
}

/**
 * The badge text: what the scan found, in one line.
 *
 * <p>Score is rounded to a whole number. The sqrt weighting produces figures like 52.03, and a
 * badge is not the place for two decimal places of a unitless score - the pivot count is the part a
 * reader acts on.
 */
export function formatSpiralBadge(zone) {
  if (!zone) return null;
  const score = Math.round(zone.totalWeightScore ?? 0);
  return `Φ Reversal Zone: ${zone.convergenceCount}-Pivot Hit (Score: ${score})`;
}

/**
 * How a zone sits relative to the window's reference point, in words.
 *
 * <p>The signed day count is the number, but "-3d" is not what a reader wants on a chip. What
 * matters is which side of the reference it falls on, because a zone that has already passed can
 * be CHECKED against the bars — it is the one kind of evidence this study can actually be held to.
 *
 * <p><b>The reference is not always now.</b> A window anchored on a clicked bar reports zones
 * around THAT bar, and describing one as "8025d ago" is true of today and useless about the
 * question asked — the reader wants to know it landed three days after the turn they clicked. So
 * `fromAnchor` switches to the distance from the anchor, which the backend sends alongside. The
 * two fields are separate on the wire for exactly this reason; overloading one would have made
 * every existing "3d ago" silently mean something else the moment an anchor was passed.
 *
 * @param {object} zone
 * @param {boolean} [fromAnchor] measure from the window's anchor rather than from the last bar
 */
export function describeZoneTiming(zone, fromAnchor = false) {
  // Falls back to the last-bar figure when the anchor one is absent, so a response from a backend
  // that predates the anchored window still reads rather than going blank.
  const days = fromAnchor && typeof zone?.daysFromAnchorBar === 'number'
    ? zone.daysFromAnchorBar
    : zone?.daysFromLastBar;
  if (typeof days !== 'number') return null;
  const n = Math.abs(Math.round(days));
  if (n === 0) return fromAnchor ? 'on the bar' : 'today';
  return days < 0 ? `${n}d ago` : `in ${n}d`;
}

/**
 * The badge line for a set of near-term zones.
 *
 * <p>Leads with the strongest, and says how many others there are rather than listing them — the
 * chip has room for one reading, and the tooltip carries the rest.
 *
 * @param {boolean} [fromAnchor] see {@link describeZoneTiming}
 */
export function formatNearTermBadge(zones, fromAnchor = false) {
  if (!Array.isArray(zones) || zones.length === 0) return null;
  const lead = zones[0];
  const when = describeZoneTiming(lead, fromAnchor);
  const score = Math.round(lead.totalWeightScore ?? 0);
  const others = zones.length - 1;
  return `Φ: ${lead.convergenceCount} pivots ${when ? `${when} ` : ''}(score ${score})`
    + (others > 0 ? ` +${others} more` : '');
}

/**
 * How far past the last bar the "reversal zones near now" window reaches, in calendar days.
 *
 * <p>The wider admin window is more useful and noisier in the same breath. The SPY case in the
 * project notes has a 6-pivot zone 34 days out — just past the 30-day default and therefore
 * invisible — so the shorter window demonstrably hides real convergences. It also admits more weak
 * ones, and the palette can only colour six zones before it starts dropping them: at a year the
 * badge's "N weaker zones are not drawn" line stops being an edge case and becomes the norm, which
 * is the cost of the longer horizon and the reason it is not the default for everyone.
 *
 * <p>The lookBACK is left to the backend. It is the half that makes a zone checkable — the bars
 * exist, so you can see what price did — and there is no reason for it to differ by who is asking.
 */
export const SPIRAL_LOOKAHEAD_DAYS = { admin: 365, user: 30 };

/**
 * How far ahead the near-term window should reach: to the last bar actually painted.
 *
 * <h2>Why not a constant</h2>
 * The window used to be a flat 365 days for an admin and 30 for everyone else, while the canvas
 * paints 250 future bars — roughly 362 calendar days. The two numbers were unrelated, so a zone
 * could land on a bar that is drawn, visible, and sitting right there on the chart, and still fall
 * outside the window that decides what gets a band. Reading the horizon off the chart itself
 * removes the discrepancy rather than trying to keep two constants in step.
 *
 * <h2>Falling back</h2>
 * Study mode paints no future bars at all (`extendBarData(..., 0)`), which would compute a
 * lookahead of zero and hide every upcoming zone. When there is nothing painted ahead — or the
 * dates are unusable — the role default stands, which is the behaviour this replaces.
 *
 * @param {string[]} extendedDates the chart's dates including painted future bars
 * @param {string[]} realDates     the dates that came from real price history
 * @param {number}   fallbackDays  the role default to use when nothing is painted ahead
 * @returns {number} calendar days from the last real bar to the last painted bar
 */
export function lookaheadToLastPaintedBar(extendedDates, realDates, fallbackDays) {
  const last = (arr) => (Array.isArray(arr) && arr.length ? arr[arr.length - 1] : null);
  const lastPainted = last(extendedDates);
  const lastReal = last(realDates);
  if (!lastPainted || !lastReal) return fallbackDays;

  // Dates arrive as 'YYYY-MM-DD' or a full ISO string; only the day part is meaningful here.
  const dayMs = 24 * 60 * 60 * 1000;
  const painted = Date.parse(String(lastPainted).slice(0, 10));
  const real = Date.parse(String(lastReal).slice(0, 10));
  if (!Number.isFinite(painted) || !Number.isFinite(real)) return fallbackDays;

  const days = Math.round((painted - real) / dayMs);
  // Zero or negative means nothing is painted past the data, so the role default applies.
  return days > 0 ? days : fallbackDays;
}
